import { shuffle } from 'lodash-es'
import type { NewsArticle } from '../../server/models/news/news-article'
import type { Article } from '../../types'
import type {
  DeepPartial,
  ArticleGroupDetails,
} from '../../composables/feature-flags/index.types'
import type { SiderailArticleGroupProps } from '../../components/ArticleSiderail/ArticleSiderail.vue'

const DEFAULT_ARTICLES_PER_GROUP = 3
const DEFAULT_ARTICLE_TYPE = 'related-stories'
const DEFAULT_DESIGN = 'google'

export function convertToArticle(article: NewsArticle): Article {
  return {
    id: article?.id,
    articleId: article?.articleId,
    thumbnailUrl: article?.thumbnailUrl ?? '',
    headline: article?.title,
    publisherIconUrl: article?.providerLogoUrl,
    publisherName: article?.provider,
    publishedDate: article?.publishedDate,
    articleUrl: article?.link || '',
    articleCategory: article?.category,
  }
}

export function getRandomNumberInArray(length: number): number {
  return Math.floor(Math.random() * length)
}

export function getArticlesForSiderail(
  headlines: NewsArticle[],
  type: 'related-stories' | 'top-stories' = 'related-stories',
  length: number,
  shownArticles: string[] = []
): Article[] {
  if (headlines.length === 0) {
    return []
  }

  // Bigger cluster size, more media coverage
  if (type === 'top-stories') {
    const articles = headlines.sort(
      (a: NewsArticle, b: NewsArticle) =>
        (b.clusterSize ?? 0) - (a.clusterSize ?? 0)
    )
    let articleIndex = 0

    return Array.from({ length }, () => {
      let article: NewsArticle

      do {
        article = articles[articleIndex]
        articleIndex++
      } while (
        shownArticles.includes(article.id) &&
        articleIndex < articles.length
      )

      shownArticles.push(article.id)
      return convertToArticle(article)
    })
  }

  // Otherwise, "related-stories"
  return Array.from({ length }, () => {
    let article: NewsArticle
    let i = headlines.length

    const shuffledHeadlines = shuffle(headlines)

    do {
      article = shuffledHeadlines[i % headlines.length]
      i--
    } while (shownArticles.includes(article.id) && i > 0)

    shownArticles.push(article.id)

    return convertToArticle(article)
  })
}

export function createArticleGroup(
  headlines: NewsArticle[],
  groupFeatureFlags: DeepPartial<ArticleGroupDetails | undefined>,
  pageDesign: DeepPartial<'google' | 'msn'>,
  articleIds: string[] = []
): SiderailArticleGroupProps | undefined {
  if (!groupFeatureFlags) {
    return undefined
  }
  return {
    design: groupFeatureFlags.design ?? pageDesign ?? DEFAULT_DESIGN,
    type: groupFeatureFlags.type ?? DEFAULT_ARTICLE_TYPE,
    articles: getArticlesForSiderail(
      headlines,
      groupFeatureFlags.type ?? DEFAULT_ARTICLE_TYPE,
      groupFeatureFlags.articlesPerGroup ?? DEFAULT_ARTICLES_PER_GROUP,
      articleIds
    ),
  }
}
